import { AsyncPipe, CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { Router, RouterModule } from '@angular/router';

import { Store } from '@ngrx/store';

import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';

import { RoleAccessDirective } from '../../@core/directives/role-access.directive';
import { FiltersAndPaginationPayloadService } from '../../@core/services/filters-and-pagination-payload.service';
import { QuantityBreakLabelService } from '../../@core/services/quantity-break-label.service';
import { OrdersActions } from '../../@store/orders/orders.actions';
import { singleOrderDetails } from '../../@store/orders/orders.model';
import {
  selectOrdersDueToday,
  selectOrdersDueTodayError,
  selectOrdersDueTodayLength,
  selectOrdersDueTodayLoading,
  selectOrdersDueTodayTotalPrice,
  selectPastDueOrders,
  selectPastDueOrdersError,
  selectPastDueOrdersLength,
  selectPastDueOrdersLoading,
  selectPastDueOrdersTotalPrice,
} from '../../@store/orders/orders.selectors';
import { OrderTableData } from '../../@store/orders/orders.state.interface';
import { QuotesActions } from '../../@store/quotes/quotes.actions';
import { selectCustomers, selectStatuses } from '../../@store/quotes/quotes.selectors';
import { Customer } from '../../@store/quotes/quotes.state.interface';
import { selectIsOrdersManager } from '../../@store/user/user.selectors';
import { CONFIG } from '../../config';
import { SafeAny } from '../../types/safe-any';
import { PaginationComponent } from '../@shared/pagination/pagination.component';

@Component({
  standalone: true,
  selector: 'ciwc-app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  imports: [
    CommonModule,
    MatTabsModule,
    RouterModule,
    MatTableModule,
    PaginationComponent,
    MatPaginatorModule,
    MatSortModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatButtonModule,
    AsyncPipe,
    RoleAccessDirective,
    MatIconModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly quantityBreakLabelService = inject(QuantityBreakLabelService);
  private readonly filtersAndPaginationPayloadService = inject(FiltersAndPaginationPayloadService);
  readonly pastDueOrders = this.store.selectSignal(selectPastDueOrders);
  readonly pastDueOrdersLoading = this.store.selectSignal(selectPastDueOrdersLoading);
  readonly pastDueOrdersError = this.store.selectSignal(selectPastDueOrdersError);
  readonly pastDueOrdersLength = this.store.selectSignal(selectPastDueOrdersLength);
  readonly pastDueOrdersTotalPrice = this.store.selectSignal(selectPastDueOrdersTotalPrice);
  readonly ordersDueTodayTotalPrice = this.store.selectSignal(selectOrdersDueTodayTotalPrice);
  readonly ordersDueToday = this.store.selectSignal(selectOrdersDueToday);
  readonly ordersDueTodayLoading = this.store.selectSignal(selectOrdersDueTodayLoading);
  readonly ordersDueTodayError = this.store.selectSignal(selectOrdersDueTodayError);
  readonly ordersDueTodayLength = this.store.selectSignal(selectOrdersDueTodayLength);
  readonly customers = this.store.selectSignal(selectCustomers);
  readonly statuses = this.store.selectSignal(selectStatuses);
  readonly isOrdersManager = this.store.selectSignal(selectIsOrdersManager);
  readonly CONFIG = CONFIG;

  // Filter controls
  clientFilterControl = new FormControl('');
  statusFilterControl = new FormControl('');
  filteredOptions$: Observable<SafeAny> | undefined;

  clientId: string | null = null;
  statusKey: string | null = null;

  dataSource: MatTableDataSource<OrderTableData> = new MatTableDataSource<OrderTableData>();
  currentRouteTab = 0;
  selectedOrdersTab = 0;
  displayedColumns: string[] = [
    'orderId',
    'description',
    'clientPurchaseOrder',
    'caNumber',
    'promisedDate',
    'dateCreated',
    'status',
    'client',
    'assignee',
    'totalQuantity',
    'orderTotalPrice',
    'dateUpdated',
    'actions',
  ];
  pageSizeOptions = CONFIG.PAGE_SIZE_OPTIONS;
  pageSizePastDueOrders = CONFIG.PAGE_SIZE;
  pageIndexPastDueOrders = 0;
  pageSizeOrdersDueToday = CONFIG.PAGE_SIZE;
  pageIndexOrdersDueToday = 0;
  totalOrders = 0;
  errorText = CONFIG.ERROR_MESSAGES.DEFAULT;
  currentSort: Sort = { active: 'promisedDate', direction: 'desc' };
  todayDate = new Date().toLocaleDateString('en-US');
  yesterdayDate = new Date(new Date().setDate(new Date().getDate() - 1)).toLocaleDateString(
    'en-US',
  );

  constructor() {
    effect(() => {
      this.initValues();
      this.hideQuantityAndPriceForManager();
    });
  }

  ngOnInit(): void {
    this.currentRouteTab = this.router.url.includes('my-orders') ? 1 : 0;
    this.listenToRouteForTabChange();
    this.fetchData();
    this.loadData();
    this.listenToFormChanges();
  }

  displayFn = (customer: Customer | string | null): string => {
    if (!customer) return '';
    if (typeof customer === 'string') return customer;

    return `${customer.firstName} ${customer.lastName}`;
  };

  onTabRouteChange(index: number): void {
    this.currentRouteTab = index;
    const route = index === 0 ? 'my-quotes' : 'my-orders';
    this.router.navigate(['/home', route]);

    if (index === 0) {
      this.clientFilterControl.setValue('');
      this.clientId = null;
    }
  }

  onOrdersTabChange(index: number): void {
    this.selectedOrdersTab = index;
    this.fetchData();
  }

  handlePageEvent(event: PageEvent): void {
    if (this.selectedOrdersTab === 0) {
      this.pageSizePastDueOrders = event.pageSize;
      this.pageIndexPastDueOrders = event.pageIndex;
    } else {
      this.pageSizeOrdersDueToday = event.pageSize;
      this.pageIndexOrdersDueToday = event.pageIndex;
    }

    const formattedSort =
      this.currentSort.direction === 'desc'
        ? `-${this.currentSort.active}`
        : this.currentSort.active;

    if (this.selectedOrdersTab === 0) {
      this.store.dispatch(
        OrdersActions.loadPastDueOrders({
          payload: {
            offset: this.pageIndexPastDueOrders + 1,
            limit: this.pageSizePastDueOrders,
            sort: formattedSort,
            toDate: this.yesterdayDate,
            archived: false,
          },
        }),
      );
    } else {
      this.store.dispatch(
        OrdersActions.loadOrdersDueToday({
          payload: {
            offset: this.pageIndexOrdersDueToday + 1,
            limit: this.pageSizeOrdersDueToday,
            sort: formattedSort,
            toDate: this.todayDate,
            fromDate: this.todayDate,
            archived: false,
          },
        }),
      );
    }
  }

  sortData(sort: Sort): void {
    this.currentSort = sort;
    const formattedSort = sort.direction === 'desc' ? `-${sort.active}` : sort.active;

    if (this.selectedOrdersTab === 0) {
      this.store.dispatch(
        OrdersActions.loadPastDueOrders({
          payload: {
            offset: this.pageIndexPastDueOrders + 1,
            limit: this.pageSizePastDueOrders,
            sort: formattedSort,
            toDate: this.yesterdayDate,
            archived: false,
          },
        }),
      );
    } else {
      this.store.dispatch(
        OrdersActions.loadOrdersDueToday({
          payload: {
            offset: this.pageIndexOrdersDueToday + 1,
            limit: this.pageSizeOrdersDueToday,
            sort: formattedSort,
            toDate: this.todayDate,
            fromDate: this.todayDate,
            archived: false,
          },
        }),
      );
    }
  }

  private loadData(): void {
    this.store.dispatch(QuotesActions.loadCustomers());
  }

  private listenToFormChanges(): void {
    // Setup filtered options for searchable select
    this.filteredOptions$ = this.clientFilterControl.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      startWith(''),
      map((value) => {
        if (!value || typeof value !== 'string') {
          return this.customers();
        }
        const filterValue = value.toLowerCase();

        return this.customers()?.filter((customer: Customer) => {
          const fullName = `${customer.firstName} ${customer.lastName}`.toLowerCase();

          return fullName.includes(filterValue);
        });
      }),
    );

    // Handle client filter
    this.clientFilterControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(null),
        filter((value: SafeAny): value is SafeAny => {
          if (!value) return false;
          return typeof value === 'object' && 'id' in value;
        }),
        distinctUntilChanged(),
      )
      .subscribe((client) => {
        if (!client) {
          this.clientId = null;
          this.filtersAndPaginationPayloadService.updateMyQuotesFilterPayload({
            client: null,
          });

          return;
        }

        this.clientId = client.id;
        this.filtersAndPaginationPayloadService.updateMyQuotesFilterPayload({
          client: client.id,
        });

        this.dispatchRouteActions(this.currentRouteTab === 0);
      });

    // Handle status filter
    this.statusFilterControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), startWith(null), distinctUntilChanged())
      .subscribe((status) => {
        if (!status) {
          this.statusKey = null;
          this.filtersAndPaginationPayloadService.updateMyQuotesFilterPayload({
            status: null,
          });
          return;
        }

        this.statusKey = status;
        this.filtersAndPaginationPayloadService.updateMyQuotesFilterPayload({ status });

        this.dispatchRouteActions(this.currentRouteTab === 0);
      });
  }

  private fetchData(): void {
    if (this.selectedOrdersTab === 0) {
      this.store.dispatch(
        OrdersActions.loadPastDueOrders({
          payload: {
            offset: this.pageIndexPastDueOrders + 1,
            limit: this.pageSizePastDueOrders,
            sort: '-promisedDate',
            toDate: this.yesterdayDate,
            archived: false,
          },
        }),
      );
    } else {
      this.store.dispatch(
        OrdersActions.loadOrdersDueToday({
          payload: {
            offset: this.pageIndexOrdersDueToday + 1,
            limit: this.pageSizeOrdersDueToday,
            sort: '-promisedDate',
            toDate: this.todayDate,
            fromDate: this.todayDate,
            archived: false,
          },
        }),
      );
    }
  }

  private listenToRouteForTabChange(): void {
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: SafeAny) => {
      if (event.url === '/home/my-orders') {
        this.currentRouteTab = 1;
      } else if (event.url === '/home/my-quotes') {
        this.currentRouteTab = 0;
      }
    });
  }

  private initValues(): void {
    const pastDueOrdersTableData = this.pastDueOrders()?.map((order: singleOrderDetails) => ({
      orderId: order.orderId,
      description: order.description || '',
      clientPurchaseOrder: order.clientPurchaseOrder || '',
      caNumber: order.caNumber || '',
      dueDate: order.dueDate || '',
      dateCreated: order.dateCreated,
      status: order.status,
      client: `${order.client.firstName} ${order.client.lastName}, ${order.client.company}, ${order.client.code}`,
      assignee: order.assignee ? `${order.assignee.firstName} ${order.assignee.lastName}` : 'N/A',
      quantity: order.totalQuantity?.toString() || '',
      totalPrice: order.orderTotalPrice?.toString() || '',
      lastEdited: order.dateUpdated || order.dateCreated,
      id: order.id,
    }));

    this.dataSource.data = pastDueOrdersTableData as OrderTableData[];

    this.totalOrders = this.pastDueOrdersLength() || 0;

    const ordersDueTodayTableData = this.ordersDueToday()?.map((order: singleOrderDetails) => ({
      orderId: order.orderId,
      description: order.description || '',
      clientPurchaseOrder: order.clientPurchaseOrder || '',
      caNumber: order.caNumber || '',
      dueDate: order.dueDate || '',
      dateCreated: order.dateCreated,
      status: order.status,
      client: `${order.client.firstName} ${order.client.lastName}, ${order.client.company}, ${order.client.code}`,
      assignee: order.assignee ? `${order.assignee.firstName} ${order.assignee.lastName}` : 'N/A',
      quantity: order.totalQuantity?.toString() || '',
      totalPrice: order.orderTotalPrice?.toString() || '',
      lastEdited: order.dateUpdated || order.dateCreated,
      id: order.id,
    }));

    if (this.selectedOrdersTab === 1) {
      this.dataSource.data = ordersDueTodayTableData as OrderTableData[];
      this.totalOrders = this.ordersDueTodayLength() || 0;
    }
  }

  clearFilters(): void {
    this.clientId = null;
    this.statusKey = null;

    this.clientFilterControl.setValue('');
    this.statusFilterControl.setValue('');
    this.filtersAndPaginationPayloadService.resetMyQuotesFilters();

    this.dispatchRouteActions(this.currentRouteTab === 0);
  }

  private dispatchRouteActions(isMyQuotes: boolean): void {
    if (isMyQuotes) {
      this.store.dispatch(
        QuotesActions.loadActiveQuotes({
          payload: {
            offset: 1,
            limit: 10,
            sort: this.filtersAndPaginationPayloadService.currentSort || 'dateCreated',
            assigneeId: localStorage.getItem('id'),
            clientId: this.clientId,
            status: this.statusKey,
            archived: false,
          },
        }),
      );
    } else {
      this.store.dispatch(
        OrdersActions.loadMyOrders({
          payload: {
            offset: 1,
            limit: 10,
            sort: this.filtersAndPaginationPayloadService.currentSort || 'dateCreated',
            assignee: localStorage.getItem('id'),
            clientId: this.clientId,
            status: this.statusKey,
            archived: false,
          },
        }),
      );
    }
  }

  private hideQuantityAndPriceForManager(): void {
    if (this.isOrdersManager()) {
      this.displayedColumns = this.displayedColumns.filter(
        (column) => column !== 'quantity' && column !== 'totalPrice',
      );
    }
  }
}
